import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { z } from 'zod';

import type { Fetcher } from '@/lib/api/fetcher';
import { createWebFetcher } from '@/lib/api/fetcher';
import type { OtpRoutes, OtpTransport } from '@/lib/auth/otp';
import { createApiOtpTransport } from '@/lib/auth/otp';

/**
 * What the client app needs from outside React.
 *
 * The OTP transport was BP-01's in-browser placeholder until BP-11 R17; it is
 * now BP-04's real `/auth/client/otp/*` pair (`lib/auth/otp.ts`). Swapping
 * this one value was the whole of that change, which is what the seam was
 * for. Tests still inject the placeholder, because a screen that renders a
 * code form does not need a server to be rendered.
 */
export interface ClientDeps {
  otp: OtpTransport;
  /**
   * The raw transport, so a feature package can build its own api over it — BP-03.T6's
   * `/privacy` fetches the DPDP notice with it.
   *
   * Optional because BP-01's own tests build a `ClientDeps` without one;
   * `createClientDeps()` always sets it, and `apps/client/bp03.tsx` falls back to the real
   * browser fetcher when it is absent.
   */
  fetcher?: Fetcher;
}

/**
 * The two api calls the OTP transport makes, bound to a fetcher.
 *
 * Spelled here rather than in `lib/api/endpoints.ts` for the reason BP-00 R5
 * gives: `lib/auth` may not import `lib/api`'s route list, and these two are
 * the only routes the client `/login` screen touches. Both answer a body the
 * screen ignores — the `sid` cookie is the result — so neither is parsed.
 */
export function otpRoutes(fetcher: Fetcher): OtpRoutes {
  return {
    request: (phone) =>
      fetcher.json(
        '/v1/auth/client/otp/request',
        { method: 'POST', body: { phone_e164: phone } },
        otpIgnoredSchema,
      ),
    verify: (phone, code) =>
      fetcher.json(
        '/v1/auth/client/otp/verify',
        { method: 'POST', body: { phone_e164: phone, code } },
        otpIgnoredSchema,
      ),
  };
}

/**
 * What the two OTP routes answer, as far as this screen cares: nothing.
 *
 * `z.unknown()` rather than the real shapes. `resend_after_s` and
 * `client_id` are both real fields and the screen reads neither — the `sid`
 * cookie is what a verify produces — so pinning them here would be a second
 * place to update every time BP-04's response grows a field, in exchange for
 * validating two values nobody looks at.
 */
const otpIgnoredSchema = z.unknown();

/**
 * The client app's real dependencies.
 *
 * BP-11 R17: the OTP transport is the api's. The code is **not** surfaced on
 * screen in any profile any more — BP-04 §9 redacts it from the ledger and
 * sends it over WhatsApp, which is the whole point of the redaction.
 */
export function createClientDeps(): ClientDeps {
  const fetcher = createWebFetcher();
  return { otp: createApiOtpTransport(otpRoutes(fetcher)), fetcher };
}

const ClientDepsContext = createContext<ClientDeps | null>(null);

/** Make `deps` available to every screen below. */
export function ClientDepsProvider({
  deps,
  children,
}: {
  deps: ClientDeps;
  children: ReactNode;
}): JSX.Element {
  return <ClientDepsContext.Provider value={deps}>{children}</ClientDepsContext.Provider>;
}

/** The injected dependencies. Throws when a screen is rendered outside the provider. */
export function useClientDeps(): ClientDeps {
  const deps = useContext(ClientDepsContext);
  if (deps === null) {
    throw new Error('26AD client: this screen must be rendered inside <ClientDepsProvider>');
  }
  return deps;
}
